import { useContext, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { Box, Container } from '@components/layout/Grid';
import MetaTags from '@components/MetaTags';
import VideoSingleMetadata from '@ecosystems/VideoSingleMetadata';
import { items5 } from '@lib/faker';
import { PlayerNotFound } from '@skeletons/PlayerNotFoundSkeleton';
import PlayerSkeleton from '@skeletons/PlayerSkeleton';
import { RelatedVideoCardSkeletonV2 } from '@skeletons/RelatedVideoCardSkeleton';
import VideoSingleMetadataSkeleton from '@skeletons/VideoSingleMetadataSkeleton';
import { VIDEO_NOT_FOUND_KEY } from '@lib/constants';
import AnalyticsManager from '@lib/analytics/manager';
import {
  ResourceTypeKind,
  useVideoQuery,
  VideoDocument,
  VideoQuery,
} from '@gql/generated';
import { RelatedVideos } from '@organisms/RelatedVideos';
import { CrossPromotionSection } from '@ecosystems/cross_promotion/CrossPromotionSection';
import useFlags from '@lib/hooks/useFlags';
import { convertMinutesToHHMMSS } from '@lib/utils';
import VideoPlayer from '@organisms/video-player';
import LanguageContext from '@lib/contexts/languageContext';

// const DynamicVideoPlayer = dynamic(() => , {
//   ssr: true,
//   loading: (props) => {
//     console.log(props);
//     return <video />;
//   },
// });

const VideoNotFoundComponent = ({ removedText = null }) => (
  <Container
    as="section"
    flexWrap="wrap"
    borderRight={[1, 0, 0]}
    px={[15, 15, 0]}
    mx={-10}
    mt={[0, 0, 60]}
    mb={60}
  >
    <Box width={[1, 1, 2 / 3]} px={[0, 0, '10px']}>
      <Box mb={20} mx={[-25, -25, 0]}>
        <PlayerNotFound message={removedText} />
      </Box>
      <Box mb={20}>
        <VideoSingleMetadataSkeleton />
      </Box>
    </Box>

    <Box width={[1, 1, 1 / 3]} px={[0, 0, '20px']}>
      {items5.map((item, index) => (
        <RelatedVideoCardSkeletonV2 key={index} />
      ))}
    </Box>
  </Container>
);

const LoadingComponent = () => (
  <Container
    as="section"
    mx={-10}
    mt={[0, 0, 60]}
    mb={60}
    px={[25, 25, 10]}
    flexWrap="wrap"
    borderRight={[1, 0, 0]}
  >
    <Box width={[1, 1, 2 / 3]} px={[0, 0, '10px']}>
      <Box mb={20} mx={[-25, -25, 0]}>
        <PlayerSkeleton />
      </Box>
      <Box mb={20}>
        <VideoSingleMetadataSkeleton />
      </Box>
    </Box>

    <Box width={[1, 1, 1 / 3]} px={[0, 0, '20px']}>
      {items5.map((item, index) => (
        <RelatedVideoCardSkeletonV2 key={index} />
      ))}
    </Box>
  </Container>
);

const VideoBoxWrapper = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  > ${Box}:not(:last-child) {
    margin-right: 20px;
  }

  margin-bottom: 20px;

  /* first child */
  > ${Box}:first-child {
    flex: 2;
  }

  /* second child */
  > ${Box}:nth-child(2) {
    flex: 1;
  }

  @media (max-width: ${({ theme }) => theme.viewports.laptop}px) {
    display: block;

    > ${Box}:not(:last-child) {
      margin-right: 0;
    }
    margin-bottom: 0;
  }
`;

const VideoWrapper = styled(Box)`
  border-radius: 5px;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.viewports.laptop}px) {
    border-radius: 0;
  }
`;

const VideoPage = (props) => {
  const router = useRouter();
  const lang = useContext(LanguageContext);
  const id = router.query?.id as string;
  const [flags] = useFlags();
  const {
    loading: loadingVideos,
    error,
    data,
  } = useVideoQuery({
    variables: { id },
  });

  useEffect(() => {
    if (data?.video) {
      const video = data?.video;
      AnalyticsManager()._PIXEL_TRACK('ViewContent', false, {
        content_type: 'product',
        content_category: 'video',
        content_ids: [video.id],
      });
    }
  }, [data?.video?.id]);

  const video: VideoQuery['video'] = data?.video || props.pageProps?.ssrData;

  const isPrivateVideo = useMemo(() => {
    // if the categories includes 'videos', the video is a library video
    return !video?.categories?.includes('videos');
  }, [video?.categories]);

  // to avoid showing loading state while we already have some data loaded on ssr
  if (loadingVideos && !video) return <LoadingComponent />;

  if (!video || video?.id === VIDEO_NOT_FOUND_KEY || error)
    return <VideoNotFoundComponent removedText={video?.removedText} />;

  const instructors = video.taxonomies.instructors;

  const taxonomiesV2 = video.taxonomiesV2.filter(
    (taxonomyV2) =>
      !taxonomyV2.term ||
      !['instructors', 'genres'].includes(taxonomyV2.term?.slug)
  );

  return (
    <>
      <Head>
        <link
          key="canonical"
          rel="canonical"
          href={process.env.DOMAIN + `/${lang}/videos/${video.identifier}`}
        />

        {isPrivateVideo ? (
          <meta name="robots" content="noindex" />
        ) : (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `
            {
                "@context": "https://schema.org",
                "@type": "VideoObject",
                "name": "${video.content.title}",
                "description": "${video.content.shortDescription}",
                "thumbnailUrl": "${video.assets.thumbnailUrl}",
                "duration": "PT${convertMinutesToHHMMSS(video.meta.minutes)}",
                "contentUrl": "${video.hls}",
                "uploadDate": "${video.publishedAt}",
                "genre": ["Yoga", "Health & Fitness"],
                "isFamilyFriendly": true
            }
          `,
            }}
          ></script>
        )}
      </Head>
      <MetaTags
        title={video.content.title}
        description={video.content.shortDescription}
        thumbnailUrl={video.assets.thumbnailUrl}
        brand={'Yogobe'}
        availability={'in stock'}
        condition={'new'}
        price="0"
        currency="SEK"
        retailerId={video.taxonomies.genre.slug}
      />
      <Container mt={[0, 0, 0, 30]} mb={20} px={[10, 10, 20]}>
        <VideoBoxWrapper as="section">
          <Box
            justifyContent="center"
            alignItems="center"
            borderRadius={10}
            overflow="hidden"
          >
            <VideoWrapper br={[0, 0, 10]}>
              <VideoPlayer
                kind={video.kind}
                autoPlay={false}
                isLimited={!video.hasAccess}
                key={video.id}
                id={video.id}
                title={video.content.title}
                description={video.content.shortDescription}
                videoSources={video.player.assets}
                thumbnailImage={video.assets.thumbnailUrl}
                video={video}
                onPlayStart={() => {
                  AnalyticsManager().sendPlayVideoEvent({
                    instructor: instructors?.[0]?.slug,
                    duration: video.meta.minutes,
                    genre: video.taxonomies.genre.name,
                  });
                }}
              />
            </VideoWrapper>
            <VideoSingleMetadata
              id={video.id}
              identifier={video.identifier}
              title={video.content.title}
              description={video.content.longDescription}
              duration={video.meta.minutes}
              taxonomies={taxonomiesV2}
              instructors={video?.taxonomies.instructors || []}
              primaryStyle={video.primaryStyle}
            />
          </Box>
          <Box>
            <RelatedVideos id={id as string} />
          </Box>
        </VideoBoxWrapper>
      </Container>
      {flags?.disable_cross_promotion === true ? null : (
        <Container flexDirection="column" px={[10, 10, 20]}>
          <CrossPromotionSection
            resourceId={video.id}
            resourceType={ResourceTypeKind['Video']}
          />
        </Container>
      )}
    </>
  );
};

export const getServerSideProps = async (ctx) => {
  const { apolloClient } = ctx.req;
  const { id } = ctx.query;

  try {
    const { data } = await apolloClient.query({
      query: VideoDocument,
      variables: { id },
    });

    return {
      props: { ssrData: data?.video || null },
    };
  } catch (ex) {
    console.error({ getServerSideProps: ex });
    //
    return {
      props: {},
    };
  }
};

export default VideoPage;
